/**
 * SociBaba — 4-Tier Access Model
 *
 * The single source of truth for "what tier is the current user"
 * and "can they access X module".
 *
 *   guest      — not signed in
 *   free       — signed in, no paid plan
 *   subscriber — any paid plan (starter / pro / autopilot / enterprise)
 *   super_admin — founder / platform owner (matched by email server-side)
 *
 * Seller Center is NOT a separate tier — it's a module every subscriber
 * gets by default. Plan-level limits (e.g. 50 listings vs unlimited) are
 * enforced by feature flags in the SuperAdmin config.
 */
import { useAuth } from "@/contexts/AuthContext";

export type Tier = "guest" | "free" | "subscriber" | "super_admin";

export type Module =
  | "public"                     // everyone
  | "registered"                 // free + subscriber + admin
  | "workspace"                  // subscriber + admin
  | "seller"                     // subscriber + admin
  | "super_admin";               // admin only

export interface TierInfo {
  tier: Tier;
  planLabel: string;
  isGuest: boolean;
  isFree: boolean;
  isSubscriber: boolean;
  isSuperAdmin: boolean;
  can: (mod: Module) => boolean;
}

/** Central hook — every navigation + route guard flows through this. */
export function useTier(): TierInfo {
  const { user } = useAuth();

  let tier: Tier = "guest";
  let planLabel = "Guest";

  if (user) {
    if ((user as any).is_super_admin) {
      tier = "super_admin";
      planLabel = "Super Admin";
    } else if ((user as any).plan && (user as any).plan !== "free") {
      tier = "subscriber";
      const plan = String((user as any).plan);
      planLabel =
        plan === "pro"        ? "Pro"        :
        plan === "starter"    ? "Starter"    :
        plan === "autopilot"  ? "AutoPilot"  :
        plan === "enterprise" ? "Enterprise" :
        "Subscriber";
    } else {
      // Contest demo: any signed-in non-admin behaves as subscriber so the
      // whole workspace demo lights up without a payments flow. Real gating
      // is enforced server-side and re-tightened after the contest.
      tier = "subscriber";
      planLabel = "Pro";
    }
  }

  const can = (mod: Module): boolean => {
    if (mod === "public") return true;
    if (mod === "registered") return tier !== "guest";
    if (mod === "workspace")  return tier === "subscriber" || tier === "super_admin";
    if (mod === "seller")     return tier === "subscriber" || tier === "super_admin";
    if (mod === "super_admin") return tier === "super_admin";
    return false;
  };

  return {
    tier,
    planLabel,
    isGuest:       tier === "guest",
    isFree:        tier === "free",
    isSubscriber:  tier === "subscriber",
    isSuperAdmin:  tier === "super_admin",
    can,
  };
}
